// Behaviour trees as data (decision 0049; the thin spot "behaviour trees as data", read against Unreal's behaviour
// trees and BehaviorTree.CPP: composites, decorators and leaves, reactive vs memory composites, halting a running
// branch). A tree is JSON; the game registers the conditions, actions and scorers it names; `compileTree` checks every
// name and shape once at load; `tickTree` runs it on the sim step. All of a running tree's memory is plain data in a
// `TreeState` (saved with the game), and nothing here draws random numbers or reads the clock: the same inputs give
// the same decisions.

/** A node's result. */
export type Status = "success" | "failure" | "running";

/** A call to something the game registered: its name and its arguments from the tree. */
export interface Call {
  name: string;
  args?: Record<string, unknown>;
}

/** A tree node, as JSON. */
export type NodeJson =
  /** Children in order until one fails (sequence) or one succeeds (selector). `reactive`: re-check from the first
   *  child every tick (a condition ahead of a running action aborts it); otherwise resume the running child. */
  | { type: "sequence" | "selector"; children: NodeJson[]; reactive?: boolean; name?: string }
  /** Every child each tick: succeed when `succeed` children have succeeded ("all" or "one"), fail when the rest can't. */
  | { type: "parallel"; children: NodeJson[]; succeed?: "all" | "one"; name?: string }
  /** Score each option with a registered scorer and run the best (ties go to the earlier one); options scoring ≤ 0
   *  are skipped. Picks again whenever the chosen one isn't running. */
  | { type: "utility"; options: { score: Call; node: NodeJson }[]; name?: string }
  /** Run the child; once it finishes (success or failure), fail for `seconds` without running it. */
  | { type: "cooldown"; seconds: number; child: NodeJson; name?: string }
  /** Run the child `times` times (-1 forever) while it succeeds; a failure fails the repeat. */
  | { type: "repeat"; times: number; child: NodeJson; name?: string }
  /** Fail the child (halting it) once it has run `seconds` without finishing. */
  | { type: "timeout"; seconds: number; child: NodeJson; name?: string }
  /** Flip success and failure. */
  | { type: "invert"; child: NodeJson; name?: string }
  /** Report success (or failure) whatever the child finished with. */
  | { type: "succeed" | "fail"; child: NodeJson; name?: string }
  /** A registered condition: success when it holds. */
  | ({ type: "condition"; name: string; args?: Record<string, unknown> } & { label?: string })
  /** A registered action. */
  | ({ type: "action"; name: string; args?: Record<string, unknown> } & { label?: string })
  /** Running for `seconds`, then success. */
  | { type: "wait"; seconds: number; name?: string };

/** A tree as JSON: its id and root. */
export interface TreeJson {
  id: string;
  root: NodeJson;
}

/** One node's memory: plain data. `i` a child index or chosen option, `t` a time, `n` a count, `m` an action's own. */
export interface NodeMemory {
  i?: number;
  t?: number;
  n?: number;
  m?: Record<string, unknown>;
}

/** A running tree's state: the tree's clock, each node's memory (by node index), what ran last tick. JSON-safe. */
export interface TreeState {
  time: number;
  mem: (NodeMemory | null)[];
  /** Node indices that returned "running" on the last tick (halted when no longer reached). */
  running: number[];
  /** The last tick's path of node labels that ran, for debug views and specs. */
  trace: string[];
}

/** A registered action. `tick` returns a status; `mem` is the node's own memory (kept while it runs, cleared on halt). */
export interface ActionDef<C> {
  tick(ctx: C, args: Record<string, unknown>, mem: Record<string, unknown>, dt: number): Status;
  /** Called when a running action is aborted (a reactive branch switched, a timeout, a parallel finished). */
  halt?(ctx: C, args: Record<string, unknown>, mem: Record<string, unknown>): void;
}

/** What the game registers: conditions, actions and scorers by name. */
export interface BehaviourRegistry<C> {
  conditions: Record<string, (ctx: C, args: Record<string, unknown>) => boolean>;
  actions: Record<string, ActionDef<C> | ((ctx: C, args: Record<string, unknown>, mem: Record<string, unknown>, dt: number) => Status)>;
  scorers?: Record<string, (ctx: C, args: Record<string, unknown>) => number>;
}

/** Just the names, for validating a tree where the functions aren't at hand (a content check). */
export interface RegistryNames {
  conditions: readonly string[];
  actions: readonly string[];
  scorers?: readonly string[];
}

interface Compiled {
  index: number;
  json: NodeJson;
  label: string;
  children: Compiled[];
  /** Index past this subtree's last node. */
  end: number;
}

/** A tree checked against the registry and numbered, ready to tick. */
export interface CompiledTree<C> {
  readonly id: string;
  readonly size: number;
  readonly root: Compiled;
  readonly registry: BehaviourRegistry<C>;
}

const COMPOSITES = new Set(["sequence", "selector", "parallel"]);
const DECORATORS = new Set(["cooldown", "repeat", "timeout", "invert", "succeed", "fail"]);

/** Every problem with a tree's shape and names (empty when it is good). Paths read like `root/children/2`. */
export function validateTree(tree: TreeJson, names: RegistryNames): string[] {
  const errs: string[] = [];
  const has = (list: readonly string[] | undefined, n: string) => (list ?? []).includes(n);
  const walk = (n: NodeJson, path: string): void => {
    if (!n || typeof n !== "object" || typeof (n as { type?: unknown }).type !== "string") {
      errs.push(`${path}: not a node`);
      return;
    }
    const type = n.type;
    if (COMPOSITES.has(type)) {
      const c = (n as { children?: unknown }).children;
      if (!Array.isArray(c) || c.length === 0) errs.push(`${path}: a ${type} needs children`);
      else c.forEach((ch, i) => walk(ch as NodeJson, `${path}/children/${i}`));
      if (type === "parallel") {
        const s = (n as { succeed?: unknown }).succeed;
        if (s !== undefined && s !== "all" && s !== "one") errs.push(`${path}: succeed must be "all" or "one"`);
      }
    } else if (type === "utility") {
      const o = (n as { options?: unknown }).options;
      if (!Array.isArray(o) || o.length === 0) errs.push(`${path}: a utility needs options`);
      else
        o.forEach((opt, i) => {
          const op = opt as { score?: Call; node?: NodeJson };
          if (!op.score || typeof op.score.name !== "string") errs.push(`${path}/options/${i}: no score`);
          else if (!has(names.scorers, op.score.name)) errs.push(`${path}/options/${i}: unknown scorer "${op.score.name}"`);
          walk(op.node as NodeJson, `${path}/options/${i}/node`);
        });
    } else if (DECORATORS.has(type)) {
      const d = n as { child?: unknown; seconds?: unknown; times?: unknown };
      if (!d.child) errs.push(`${path}: a ${type} needs a child`);
      else walk(d.child as NodeJson, `${path}/child`);
      if ((type === "cooldown" || type === "timeout") && !(typeof d.seconds === "number" && d.seconds >= 0)) errs.push(`${path}: ${type} needs seconds ≥ 0`);
      if (type === "repeat" && !(typeof d.times === "number" && Number.isInteger(d.times) && (d.times === -1 || d.times > 0))) errs.push(`${path}: repeat needs times > 0 or -1`);
    } else if (type === "condition") {
      if (!has(names.conditions, (n as { name: string }).name)) errs.push(`${path}: unknown condition "${(n as { name: string }).name}"`);
    } else if (type === "action") {
      if (!has(names.actions, (n as { name: string }).name)) errs.push(`${path}: unknown action "${(n as { name: string }).name}"`);
    } else if (type === "wait") {
      if (!(typeof (n as { seconds?: unknown }).seconds === "number")) errs.push(`${path}: wait needs seconds`);
    } else errs.push(`${path}: unknown node type "${type}"`);
  };
  if (!tree || typeof tree.id !== "string") errs.push("tree: no id");
  walk(tree?.root, "root");
  return errs;
}

/** The names a registry holds. */
export function registryNames<C>(r: BehaviourRegistry<C>): RegistryNames {
  return { conditions: Object.keys(r.conditions), actions: Object.keys(r.actions), scorers: Object.keys(r.scorers ?? {}) };
}

/** Check a tree against the registry and number its nodes. Throws listing every problem. */
export function compileTree<C>(tree: TreeJson, registry: BehaviourRegistry<C>): CompiledTree<C> {
  const errs = validateTree(tree, registryNames(registry));
  if (errs.length) throw new Error(`behaviour tree "${tree?.id}": ${errs.join("; ")}`);
  let next = 0;
  const build = (n: NodeJson): Compiled => {
    const index = next++;
    const label =
      n.type === "condition" || n.type === "action" ? `${n.type === "condition" ? "?" : ""}${n.label ?? n.name}` : `${n.type}${(n as { name?: string }).name ? `:${(n as { name?: string }).name}` : ""}`;
    const kids: NodeJson[] =
      n.type === "sequence" || n.type === "selector" || n.type === "parallel"
        ? n.children
        : n.type === "utility"
          ? n.options.map((o) => o.node)
          : "child" in n
            ? [n.child]
            : [];
    const children = kids.map(build);
    return { index, json: n, label, children, end: next };
  };
  const root = build(tree.root);
  return { id: tree.id, size: next, root, registry };
}

/** A fresh state for a compiled tree. */
export function createTreeState(tree: CompiledTree<unknown> | { size: number }): TreeState {
  return { time: 0, mem: new Array<NodeMemory | null>(tree.size).fill(null), running: [], trace: [] };
}

/** Planted defects for the runner's own tests (none in play). */
export const BT_FAULTS = {
  /** Composites forget their running child: a memory sequence restarts from its first child every tick. */
  forgetRunning: false,
};

/** Tick the tree once (on the sim step, `dt` seconds since the last tick). Returns the root's status. */
export function tickTree<C>(tree: CompiledTree<C>, state: TreeState, ctx: C, dt: number): Status {
  state.time += dt;
  const ran = new Set<number>();
  const trace: string[] = [];
  const memOf = (c: Compiled): NodeMemory => (state.mem[c.index] ??= {});
  const reg = tree.registry;

  const halt = (c: Compiled): void => {
    const m = state.mem[c.index];
    if (c.json.type === "action" && m?.m) {
      const a = reg.actions[c.json.name]!;
      if (typeof a !== "function") a.halt?.(ctx, c.json.args ?? {}, m.m);
    }
    // a cooldown's clock survives a halt (it is a property of the node, not of the run)
    if (c.json.type === "cooldown" && m?.t !== undefined) state.mem[c.index] = { t: m.t };
    else state.mem[c.index] = null;
    for (const ch of c.children) halt(ch);
  };
  const haltRunningIn = (c: Compiled): void => {
    // halt every node in this subtree that was running last tick and wasn't reached this tick
    for (const i of state.running) if (i >= c.index && i < c.end && !ran.has(i)) {
      const n = find(c, i);
      if (n) halt(n);
    }
  };
  const find = (c: Compiled, i: number): Compiled | null => {
    if (c.index === i) return c;
    for (const ch of c.children) if (i >= ch.index && i < ch.end) return find(ch, i);
    return null;
  };

  const run = (c: Compiled): Status => {
    ran.add(c.index);
    trace.push(c.label);
    const n = c.json;
    const mem = memOf(c);
    let s: Status;
    switch (n.type) {
      case "sequence":
      case "selector": {
        const stopOn: Status = n.type === "sequence" ? "failure" : "success";
        const start = n.reactive || BT_FAULTS.forgetRunning ? 0 : (mem.i ?? 0);
        s = n.type === "sequence" ? "success" : "failure";
        for (let k = start; k < c.children.length; k++) {
          const r = run(c.children[k]!);
          if (r === "running") {
            // a reactive composite that moved to another branch halts the one that was running
            if (n.reactive && mem.i !== undefined && mem.i !== k) halt(c.children[mem.i]!);
            mem.i = k;
            s = "running";
            break;
          }
          if (r === stopOn) {
            s = r;
            break;
          }
        }
        // a finished composite forgets its place (a branch it abandoned is halted after the tick)
        if (s !== "running") state.mem[c.index] = null;
        break;
      }
      case "parallel": {
        let ok = 0;
        let bad = 0;
        for (const ch of c.children) {
          const r = run(ch);
          if (r === "success") ok++;
          else if (r === "failure") bad++;
        }
        const need = n.succeed === "one" ? 1 : c.children.length;
        if (ok >= need) s = "success";
        else if (c.children.length - bad < need) s = "failure";
        else s = "running";
        if (s !== "running") for (const ch of c.children) halt(ch);
        break;
      }
      case "utility": {
        let pick = mem.i;
        if (pick === undefined) {
          let best = 0;
          for (let k = 0; k < n.options.length; k++) {
            const o = n.options[k]!;
            const fn = reg.scorers?.[o.score.name];
            const v = fn ? fn(ctx, o.score.args ?? {}) : 0;
            if (v > best) {
              best = v;
              pick = k;
            }
          }
        }
        if (pick === undefined) {
          s = "failure";
          break;
        }
        s = run(c.children[pick]!);
        if (s === "running") mem.i = pick;
        else state.mem[c.index] = null;
        break;
      }
      case "cooldown": {
        if (mem.t !== undefined && state.time < mem.t) {
          s = "failure";
          break;
        }
        s = run(c.children[0]!);
        if (s !== "running") mem.t = state.time + n.seconds;
        break;
      }
      case "repeat": {
        s = run(c.children[0]!);
        if (s === "success") {
          mem.n = (mem.n ?? 0) + 1;
          if (n.times === -1 || mem.n < n.times) s = "running";
          else state.mem[c.index] = null;
        } else if (s === "failure") state.mem[c.index] = null;
        break;
      }
      case "timeout": {
        mem.t ??= state.time - dt; // the tick it starts on counts
        s = run(c.children[0]!);
        if (s === "running" && state.time - mem.t >= n.seconds - 1e-9) {
          halt(c.children[0]!);
          s = "failure";
        }
        if (s !== "running") state.mem[c.index] = null;
        break;
      }
      case "invert": {
        const r = run(c.children[0]!);
        s = r === "running" ? r : r === "success" ? "failure" : "success";
        break;
      }
      case "succeed":
      case "fail": {
        const r = run(c.children[0]!);
        s = r === "running" ? r : n.type === "succeed" ? "success" : "failure";
        break;
      }
      case "condition":
        s = reg.conditions[n.name]!(ctx, n.args ?? {}) ? "success" : "failure";
        break;
      case "action": {
        const a = reg.actions[n.name]!;
        mem.m ??= {};
        s = typeof a === "function" ? a(ctx, n.args ?? {}, mem.m, dt) : a.tick(ctx, n.args ?? {}, mem.m, dt);
        if (s !== "running") state.mem[c.index] = null;
        break;
      }
      case "wait": {
        mem.t ??= state.time - dt;
        s = state.time - mem.t >= n.seconds - 1e-9 ? "success" : "running";
        if (s !== "running") state.mem[c.index] = null;
        break;
      }
    }
    if (s !== "running" && state.mem[c.index] && c.json.type !== "cooldown") state.mem[c.index] = null;
    if (s === "running") runningNow.push(c.index);
    return s;
  };

  const runningNow: number[] = [];
  const s = run(tree.root);
  // whatever ran last tick and wasn't reached now was abandoned: halt it
  haltRunningIn(tree.root);
  state.running = runningNow.sort((a, b) => a - b);
  state.trace = trace;
  return s;
}
